import React, { useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {
  FiBookOpen,
  FiHeart,
  FiTarget,
  FiCheckSquare,
  FiFeather,
  FiCalendar
} = FiIcons

const FaithGrowth = () => {
  const [selectedDay, setSelectedDay] = useState(0)
  const [prayerEntry, setPrayerEntry] = useState('')
  const [prayerJournal, setPrayerJournal] = useState([
    { date: '2024-11-04', entry: 'Grateful for a calm morning and courage in math.' }
  ])
  const [goals, setGoals] = useState([
    { id: 1, title: 'Listen with patience during group time', progress: 70 },
    { id: 2, title: 'Memorize this week’s verse', progress: 40 }
  ])

  const readings = [
    { title: 'Psalm 23', focus: 'God is our Shepherd' },
    { title: 'Proverbs 3:5-6', focus: 'Trust and guidance' },
    { title: 'John 15:12', focus: 'Loving one another' },
    { title: 'Philippians 4:8', focus: 'Thought life' },
    { title: 'Ephesians 6:10-11', focus: 'Strength in God' }
  ]

  const traitFocus = {
    trait: 'Perseverance',
    practice: 'Finish a hard task before taking a break, and reflect on what helped.',
    scripture: 'Galatians 6:9',
    conversation: 'Where did you keep going this week?'
  }

  const addPrayerEntry = () => {
    if (!prayerEntry.trim()) return
    setPrayerJournal((prev) => [
      { date: new Date().toISOString().slice(0, 10), entry: prayerEntry },
      ...prev
    ])
    setPrayerEntry('')
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#1B4C87] to-[#00AAFF] rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-3">Faith & Personal Growth</h1>
        <p className="text-blue-50 max-w-3xl">
          Guided Bible readings, character traits, and growth goals with prayer and gratitude
          journaling to integrate heart and mind.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bible Reading */}
        <div className="bg-white rounded-xl shadow-lg p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <SafeIcon icon={FiBookOpen} className="w-5 h-5 mr-2 text-[#00AAFF]" />
              Bible Study - Daily Readings
            </h2>
            <div className="flex items-center text-sm text-gray-600">
              <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1" />
              Week at a glance
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {readings.map((reading, index) => (
              <button
                key={reading.title}
                className={`p-3 rounded-lg border-2 text-left transition ${
                  selectedDay === index
                    ? 'border-[#00AAFF] bg-[#00AAFF]/10'
                    : 'border-gray-200 bg-gray-50 hover:border-[#1B4C87]'
                }`}
                onClick={() => setSelectedDay(index)}
              >
                <p className="text-xs text-gray-500">Day {index + 1}</p>
                <p className="font-semibold text-gray-800">{reading.title}</p>
                <p className="text-sm text-gray-600">{reading.focus}</p>
              </button>
            ))}
          </div>
          {readings[selectedDay] && (
            <div className="mt-4 p-4 rounded-lg bg-[#1B4C87]/10 text-[#0C3B72]">
              <p className="text-sm font-semibold">Meditation focus</p>
              <p className="text-sm">
                Consider how &ldquo;{readings[selectedDay].focus}&rdquo; shapes your choices today.
                Write or draw one way you can live this out.
              </p>
            </div>
          )}
        </div>

        {/* Character Trait */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiHeart} className="w-5 h-5 text-[#F43F5E]" />
            <h3 className="text-lg font-bold text-gray-800">Character Trait Focus</h3>
          </div>
          <p className="text-sm text-gray-700">Trait: <span className="font-semibold">{traitFocus.trait}</span></p>
          <p className="text-sm text-gray-700">Practice: {traitFocus.practice}</p>
          <p className="text-sm text-gray-700">Scripture: {traitFocus.scripture}</p>
          <p className="text-sm text-gray-700">Conversation: {traitFocus.conversation}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Growth Goals */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 flex items-center mb-4">
            <SafeIcon icon={FiTarget} className="w-5 h-5 mr-2 text-[#00AAFF]" />
            Personal Growth Goals
          </h3>
          <div className="space-y-4">
            {goals.map((goal) => (
              <div key={goal.id} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                <p className="font-semibold text-gray-800">{goal.title}</p>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-[#00AAFF] to-[#1B4C87]"
                    style={{ width: `${goal.progress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">{goal.progress}% complete</p>
              </div>
            ))}
          </div>
        </div>

        {/* Prayer Journal */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiFeather} className="w-5 h-5 mr-2 text-[#1B4C87]" />
            Prayer & Gratitude Journal
          </h3>
          <textarea
            className="w-full border border-gray-200 rounded-lg px-3 py-2"
            rows="4"
            placeholder="Write a prayer or gratitude note..."
            value={prayerEntry}
            onChange={(e) => setPrayerEntry(e.target.value)}
          />
          <button
            onClick={addPrayerEntry}
            className="bg-[#00AAFF] text-white py-2 rounded-lg hover:bg-[#0095E0] transition"
          >
            Add Journal Entry
          </button>
          <div className="space-y-3">
            {prayerJournal.map((entry, index) => (
              <div key={`${entry.date}-${index}`} className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>{entry.date}</span>
                  <SafeIcon icon={FiCheckSquare} className="w-4 h-4 text-[#10B981]" />
                </div>
                <p className="text-sm text-gray-800 whitespace-pre-line">{entry.entry}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FaithGrowth
